import React, { useState, useEffect } from "react";
import { useFormikContext, FastField } from "formik";
import { Row, Col, Form } from "react-bootstrap";
import { Upload, ChevronDown, ChevronUp } from "lucide-react";
import { QuotationFormValues } from "../initialValues";
import { ServiceQuotationFormValues } from "../serviceInitialValues";
import { MecordColors } from "Constants/Color";

// Represents either an existing attachment (URL string) or a new file
interface AttachmentItem {
  id: string;
  name: string;
  isExisting: boolean; // true if it's a URL from backend, false if it's a new File
  file?: File; // Only present for new files
  url?: string; // Only present for existing attachments
}

const NotesAttachmentsSection: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<
    QuotationFormValues | ServiceQuotationFormValues
  >();
  const [attachmentItems, setAttachmentItems] = useState<AttachmentItem[]>([]);
  const [isNotesOpen, setIsNotesOpen] = useState(true);

  // Auto-open notes section if notes have content
  React.useEffect(() => {
    if (values.notes && values.notes.trim() !== "") {
      setIsNotesOpen(true);
    }
  }, [values.notes]);

  // Keep displayed attachments in sync with form values, including async enquiry autofill.
  useEffect(() => {
    if (!values.attachments || values.attachments.length === 0) {
      setAttachmentItems([]);
      return;
    }

    const existingAttachments: AttachmentItem[] = values.attachments.map((attachment: any, index: number) => {
      if (typeof attachment === 'string') {
        const fileName = attachment.split('/').pop() || `Attachment ${index + 1}`;
        return {
          id: `existing-${index}-${attachment}`,
          name: fileName,
          isExisting: true,
          url: attachment,
        };
      } else if (attachment instanceof File) {
        return {
          id: `file-${attachment.name}-${attachment.size}-${attachment.lastModified}`,
          name: attachment.name,
          isExisting: false,
          file: attachment,
        };
      }
      return null;
    }).filter(Boolean) as AttachmentItem[];

    setAttachmentItems(existingAttachments);
  }, [values.attachments]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      const newAttachmentItems: AttachmentItem[] = newFiles.map((file) => ({
        id: `file-${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        name: file.name,
        isExisting: false,
        file,
      }));

      const updatedItems = [...attachmentItems, ...newAttachmentItems];
      setAttachmentItems(updatedItems);

      // Update form values - keep existing URLs and add new Files
      const formAttachments = updatedItems.map((item) => 
        item.isExisting ? item.url : item.file
      );
      setFieldValue("attachments", formAttachments);

      // Reset input to allow selecting the same files again
      e.target.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    const newItems = attachmentItems.filter((_, i) => i !== index);
    setAttachmentItems(newItems);

    // Update form values
    const formAttachments = newItems.map((item) => 
      item.isExisting ? item.url : item.file
    );
    setFieldValue("attachments", formAttachments);
  };

  return (
    <Row className="g-3 mb-3">
      <Col xs={12} md={12}>
        <div className="d-flex justify-content-between align-items-center mb-1 gap-2">
          <h5 className="fw-semibold">Notes & Attachments</h5>
        </div>

        <div
          className="rounded"
          style={{ backgroundColor: MecordColors?.LightGrayColor }}
        >
          {/* Notes Header - Collapsible */}
          <div
            className="d-flex justify-content-start align-items-center p-2 rounded"
            style={{
              cursor:
                !values.notes || values.notes.trim() === ""
                  ? "pointer"
                  : "default",
              backgroundColor: MecordColors?.LightGrayColor,
            }}
            onClick={() => {
              // Only allow closing if notes are empty
              if (!values.notes || values.notes.trim() === "") {
                setIsNotesOpen(!isNotesOpen);
              }
            }}
          >
            {(!values.notes || values.notes.trim() === "") && (
              <div className="me-1">
                {isNotesOpen ? (
                  <ChevronUp
                    size={20}
                    // style={{ color: MecordColors?.LightGrayColor }}
                  />
                ) : (
                  <ChevronDown
                    size={20}
                    // style={{ color: MecordColors?.LightGrayColor }}
                  />
                )}
              </div>
            )}

            <h6 className="fw-semibold mb-0" style={{ fontSize: "0.9rem" }}>
              Notes
            </h6>
          </div>

          {/* Notes Content - Collapsible */}
          {isNotesOpen && (
            <div className="p-0">
              <Form.Group>
                <FastField name="notes">
                  {({ field }: any) => (
                    <Form.Control
                      {...field}
                      as="textarea"
                      rows={4}
                      style={{ maxHeight: '400px', resize: 'vertical' }}
                      placeholder="Enter your notes"
                    />
                  )}
                </FastField>
              </Form.Group>
            </div>
          )}
        </div>
      </Col>

      <Col xs={12} md={12}>
        <div
          className="rounded "
          // style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
        >
          <div className="d-flex justify-content-between align-items-center mb-1 gap-2">
            <h5 className="fw-semibold">Attach Files</h5>
          </div>
          <Form.Group className="mb-3">
            <div className="position-relative">
              <Form.Control
                type="file"
                multiple
                onChange={(e) =>
                  handleFileChange(e as React.ChangeEvent<HTMLInputElement>)
                }
                style={{ display: "none" }}
                id="file-input"
              />
              <Form.Label
                htmlFor="file-input"
                className="w-100 d-flex align-items-center justify-content-between p-2 border rounded"
                style={{ cursor: "pointer" }}
              >
                <span className="text-muted">
                  {attachmentItems.length > 0
                    ? `${attachmentItems.length} file(s) attached`
                    : "Attach Files"}
                </span>
                <Upload size={18} />
              </Form.Label>
            </div>
            {attachmentItems.length > 0 && (
              <div className="mt-2">
                <div className="d-flex flex-column gap-2">
                  {attachmentItems.map((item, index) => (
                    <div
                      key={item.id}
                      className="d-flex align-items-center justify-content-between p-2 border rounded"
                      style={{
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      <span
                        className="text-truncate"
                        style={{ 
                          fontSize: "0.875rem", 
                          maxWidth: "calc(100% - 40px)",
                          color: item.isExisting ? "#0d6efd" : "inherit",
                          cursor: item.isExisting ? "pointer" : "default",
                        }}
                        title={item.name}
                        onClick={() => {
                          if (item.isExisting && item.url) {
                            window.open(item.url, "_blank");
                          }
                        }}
                      >
                        {item.name}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => handleRemoveFile(index)}
                        style={{
                          backgroundColor: "rgba(220, 53, 69, 0.9)",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          width: "24px",
                          height: "24px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0,
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Form.Group>
        </div>
      </Col>
    </Row>
  );
};

export default NotesAttachmentsSection;
